// "Love must feel like this": a blue-grey reference photo at a paint class → sweep in her pinks, blues and purples, press a fingerprint moon → rose-coloured glasses → her painting emerges, stroke by stroke.
export const love = {
  slug: "love-must-feel-like-this",
  duration: 24,
  music: "reverie",
  musicAt: 13,
  captionTail: ["It was meant to be a copy, in blue and grey moonlight. I painted it like me.", "When you're in love, it cannot be black and blue."],
  stages: [
    { id: "class", at: 0, hint: "the paint class" },
    { id: "colours", at: 6, hint: "drag to sweep her colours · tap for the moon" },
    { id: "rose", at: 13.5, hint: "rose-coloured glasses" },
    { id: "emerge", at: 20, hint: "watch her paint it · tap to finish" },
  ],
  touch: { kind: "sweep", from: 6, until: 13.5 },
  // the muted reference at the paint class → (her colours sweep in) → the rose-coloured dream
  layers: [
    { src: "/arcs/love/class.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, show: [-1, 0, 6.5, 9.5] },
    // her colours: pink, blue and purple swept over the grey reference, the moon pressed in with a thumbprint
    { src: "/arcs/love/paint.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, glow: 0.12, show: [5.5, 9, 13, 15.5] },
    { src: "/arcs/love/dream.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, mist: 0.1, mistCol: "#f3c9dc", show: [12.5, 15.5, 99, 99] },
  ],
  line: { text: "To be chosen must feel this dreamy: love must feel like this.", from: 16, to: 31, pos: "low" },
  cues: [[11.6, "pop"]],
  ambient: { scene: "bench", until: 99, then: [[13.5, "garden"]] },
  look: { start: 0.12, lift: [0, 1], end: 0.12, yaw: [[0, 0], [24, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "love", pos: "low" },
  speed: [[0, 1], [24, 1]],
  reveal: {
    at: 20,
    style: "circular",
    center: [0.5, 0.47],
    duration: 11,
    focus: [
      { rect: [0.25, 0.4, 0.22, 0.18], style: "flow" },
      { rect: [0.46, 0.42, 0.26, 0.2], style: "flow" },
      { rect: [0.36, 0.33, 0.28, 0.08], style: "circular" },
    ],
  },
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform vec4 uPart[24];
uniform vec4 uMark[8];
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
float branchY(float x){ return 2.2 + 0.16 * sin(x * 0.8) - 0.06 * x; }
// a small dark bird perched, facing dir, leaning by lean
float bird(vec2 p, float dir, float lean){
  p.x *= dir;
  p = mat2(cos(lean), -sin(lean), sin(lean), cos(lean)) * p;
  float body = length(vec2(p.x / 0.2, (p.y - 0.12) / 0.12));
  float head = length(p - vec2(0.16, 0.26)) / 0.075;
  float tail = step(p.x, -0.12) * step(-0.36, p.x) * step(abs(p.y - 0.06 + (p.x + 0.12) * 0.5), 0.05 + (-0.12 - p.x) * 0.15);
  float beak = step(0.22, p.x) * step(p.x, 0.28) * step(abs(p.y - 0.26), (0.28 - p.x) * 0.5);
  return max(max(step(body, 1.0), step(head, 1.0)), max(tail, beak));
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 4.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float rose = smoothstep(13.5, 17.0, uT);
  float bloom = smoothstep(14.5, 18.5, uT);
  vec3 col;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    float y = vPos.y;
    vec2 P = vec2(X, y);
    // the reference photo: muted blue-grey moonlight, slightly grainy
    vec2 M = vec2(0.0, 2.95);
    float md = length(P - M);
    vec3 grey = mix(vec3(0.36, 0.4, 0.47), vec3(0.14, 0.16, 0.22), smoothstep(0.5, 4.0, md)) * (0.94 + 0.1 * n2);
    grey = mix(grey, vec3(0.82, 0.84, 0.86), 1.0 - smoothstep(0.52, 0.56, md));
    // rose-coloured glasses: swirls of lavender, blue and pink circling out from the moon
    float sw = sin(md * 5.0 - atan(P.y - M.y, P.x) * 1.0 + snoise(vec3(P * 0.6, uT * 0.05)) * 1.6 - uT * 0.25);
    vec3 dream = mix(mix(vec3(0.72, 0.62, 0.88), vec3(0.45, 0.55, 0.9), smoothstep(-0.6, 0.2, sw)), vec3(0.95, 0.6, 0.78), smoothstep(0.3, 0.9, sw));
    dream = mix(dream, vec3(0.99, 0.97, 0.86), 1.0 - smoothstep(0.52, 0.56, md));
    col = mix(grey, dream, rose);
    // her colours, swept in with a drag: pink, blue, purple
    for (int i = 0; i < 24; i++){
      vec4 m = uPart[i];
      if (m.w <= 0.0) continue;
      vec2 q = vec2(atan(m.x, -m.z) * uRadius, m.y);
      if (length(P - q) > 1.0) continue;
      float dd = length(P - q) + 0.2 * snoise(vec3(P * 2.5, float(i)));
      float k = (1.0 - smoothstep(0.35, 0.75, dd)) * smoothstep(m.w, m.w + 0.4, uT) * (1.0 - rose * 0.6);
      float ci = mod(float(i), 3.0);
      vec3 pc = ci < 0.5 ? vec3(0.95, 0.55, 0.75) : ci < 1.5 ? vec3(0.45, 0.6, 0.95) : vec3(0.65, 0.45, 0.85);
      col = mix(col, pc, k * 0.8);
    }
    // the fingerprint moon, pressed in with a fingertip
    float pressAt = uMark[0].w > 0.0 ? uMark[0].w : 11.6;
    float pr = smoothstep(pressAt, pressAt + 0.8, uT) * step(md, 0.55);
    float whorl = 0.5 + 0.5 * sin(length((P - M) * vec2(1.0, 1.3)) * 70.0 + snoise(vec3(P * 4.0, 1.0)) * 2.0);
    col = mix(col, mix(vec3(0.98, 0.96, 0.85), vec3(0.88, 0.86, 0.95), whorl * 0.6), pr);
    // the bare branch, blossoming for you
    float by = branchY(X);
    float bw = 0.05 * (1.0 - smoothstep(0.5, 4.0, abs(X - 0.3)));
    float br = (1.0 - smoothstep(bw * 0.7, bw, abs(y - by))) * step(-4.0, X) * step(X, 3.2);
    float tw = (1.0 - smoothstep(0.008, 0.016, abs(y - by - (X - floor(X * 1.2) / 1.2) * 0.6))) * step(0.0, y - by) * step(y - by, 0.3) * step(0.4, fract(X * 1.2));
    col = mix(col, mix(vec3(0.08, 0.08, 0.1), vec3(0.25, 0.16, 0.22), rose), max(br, tw * 0.8));
    vec2 bc = vec2(X / 0.16, (y - by - 0.05) / 0.16);
    vec2 bf = fract(bc) - 0.5;
    float bh = hash1(dot(floor(bc), vec2(1.0, 57.0)));
    float pa = atan(bf.y, bf.x) + bh * 6.28;
    float blossom = (1.0 - smoothstep(0.85, 1.0, length(bf) / ((0.18 + 0.12 * cos(5.0 * pa)) * bloom * smoothstep(0.0, 0.25, bloom - bh * 0.7)))) * step(0.45, bh) * step(abs(y - by - 0.05), 0.28) * step(-4.0, X) * step(X, 3.2);
    vec3 bcol = bh < 0.62 ? vec3(0.98, 0.7, 0.82) : bh < 0.8 ? vec3(0.95, 0.95, 0.98) : bh < 0.9 ? vec3(0.98, 0.82, 0.3) : vec3(0.55, 0.65, 0.98);
    col = mix(col, bcol, blossom);
    // two birds on the branch, leaning toward each other
    float lean = smoothstep(15.0, 19.0, uT);
    float b1 = bird(P - vec2(-0.42 + 0.1 * lean, branchY(-0.42) + bw * 0.5), 1.0, -0.18 * lean);
    float b2 = bird(P - vec2(0.42 - 0.1 * lean, branchY(0.42) + bw * 0.5), -1.0, -0.18 * lean);
    col = mix(col, vec3(0.06, 0.06, 0.08) + vec3(0.12, 0.06, 0.08) * rose, max(b1, b2));
    // the breeze carries petals for you
    for (int i = 0; i < 14; i++){
      float fi = float(i);
      float ph = fract(uT * 0.07 + hash1(fi) );
      vec2 c = vec2(-6.0 + hash1(fi + 3.0) * 12.0 + ph * 5.0, 4.5 - ph * 4.0 + 0.3 * sin(uT + fi));
      float a = uT * (0.8 + hash1(fi + 5.0)) + fi;
      vec2 q = mat2(cos(a), -sin(a), sin(a), cos(a)) * (P - c);
      float petal = 1.0 - smoothstep(0.85, 1.0, length(q / vec2(0.07, 0.035)));
      col = mix(col, vec3(0.98, 0.72, 0.84), petal * rose * 0.9);
    }
    // pots of pink, blue and purple at the foot of the wall
    for (int i = 0; i < 3; i++){
      float fi = float(i);
      vec2 q = P - vec2(-0.7 + fi * 0.7, 0.35);
      float pot = step(abs(q.x), 0.18 - q.y * 0.1) * step(-0.2, q.y) * step(q.y, 0.15);
      float paint = step(abs(q.x), 0.17) * step(abs(q.y - 0.15), 0.03);
      vec3 pc = fi < 0.5 ? vec3(0.95, 0.5, 0.72) : fi < 1.5 ? vec3(0.4, 0.55, 0.95) : vec3(0.6, 0.4, 0.82);
      float on = smoothstep(5.5, 7.0, uT) * (1.0 - smoothstep(17.0, 19.0, uT));
      col = mix(col, mix(vec3(0.85, 0.82, 0.78), pc, paint), max(pot, paint) * on);
    }
  } else if (uSurface < 1.5) {
    col = mix(mix(vec3(0.2, 0.21, 0.25), vec3(0.26, 0.27, 0.3), n), mix(vec3(0.82, 0.7, 0.85), vec3(0.95, 0.78, 0.86), n), rose);
  } else {
    col = mix(vec3(0.12, 0.14, 0.2), vec3(0.7, 0.62, 0.88), rose);
  }
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
